<template>
  <div>
    <highcharts v-if="rows.length > 0" :key="hcKey" :options="chartOptions"></highcharts>
    <chart-not-available v-else :if-is-mop="false"></chart-not-available>
  </div>
</template>

<script>
import { setChartMenuItemsLang } from '../../utils/formatter'
import { formatScorePercent, TESTED_PRODUCT_COLOR } from '../../utils/driversBubbleChart'
import { getDriversBoxPlotRows } from '../../utils/driversBoxPlot'
import ChartNotAvailable from '../MarketCompCharts/ChartNotAvailable'

// "Posizionamento per driver" view of EfficiencyTestsDetails.vue: for each driver (heaviest first)
// a box plot of the comparables' values, with the tested product as an orange point on top.
export default {
  name: 'DriversBoxPlotChart',
  components: {
    ChartNotAvailable,
  },
  props: {
    // summary.drivers of the result: [{ driver, weight, ... }]
    drivers: {
      type: Array,
      default: () => [],
    },
    testedProduct: {
      type: Object,
      default: null,
    },
    comparables: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      hcKey: 0,
    }
  },
  computed: {
    rows() {
      return getDriversBoxPlotRows(this.drivers, this.testedProduct, this.comparables)
    },
    setOptions() {
      this.hcKey += 1
      return setChartMenuItemsLang()
    },
    chartOptions() {
      const vm = this
      const rows = this.rows
      const testedIsin = (this.testedProduct && this.testedProduct.isin) || ''
      return {
        lang: this.setOptions,
        chart: {
          inverted: true,
          backgroundColor: '#FFFFFF',
          height: '42%',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          filename: this.$t('efficiencyTests.boxPlotChart.title'),
        },
        title: {
          text: this.$t('efficiencyTests.boxPlotChart.title'),
        },
        legend: {
          enabled: true,
          itemStyle: { fontWeight: 'normal' },
        },
        xAxis: {
          categories: rows.map(
            (row) =>
              this.driverLabel(row.driver) +
              ' (' +
              this.$t('efficiencyTests.boxPlotChart.weight') +
              ' ' +
              formatScorePercent(row.weight || 0) +
              ')'
          ),
          title: { text: null },
        },
        yAxis: {
          min: 0,
          max: 1,
          title: { text: this.$t('efficiencyTests.boxPlotChart.driverScore') },
          labels: { formatter: (el) => formatScorePercent(el.value) },
        },
        tooltip: {
          formatter: function () {
            const row = rows[this.point.x]
            const label = vm.driverLabel(row.driver)
            if (this.series.type === 'boxplot') {
              const s = row.stats
              return (
                '<b>' +
                label +
                '</b><br>' +
                vm.$t('efficiencyTests.boxPlotChart.peers') +
                ': ' +
                s.count +
                '<br>Max: ' +
                formatScorePercent(s.high) +
                '<br>Q3: ' +
                formatScorePercent(s.q3) +
                '<br>' +
                vm.$t('efficiencyTests.boxPlotChart.median') +
                ': ' +
                formatScorePercent(s.median) +
                '<br>Q1: ' +
                formatScorePercent(s.q1) +
                '<br>Min: ' +
                formatScorePercent(s.low)
              )
            }
            const gap = Math.round((row.tested - row.stats.median) * 100)
            return (
              '<b>' +
              testedIsin +
              '</b><br>' +
              label +
              ': ' +
              formatScorePercent(row.tested) +
              '<br>' +
              vm.$t('efficiencyTests.boxPlotChart.gapFromMedian') +
              ': ' +
              (gap > 0 ? '+' : '') +
              gap +
              ' pt<br>' +
              vm.$t('efficiencyTests.boxPlotChart.position.' + row.position)
            )
          },
        },
        series: [
          {
            type: 'boxplot',
            name: this.$t('efficiencyTests.boxPlotChart.peers'),
            data: rows.map((row) => [row.stats.low, row.stats.q1, row.stats.median, row.stats.q3, row.stats.high]),
            color: '#378ADD',
            fillColor: 'rgba(55, 138, 221, 0.25)',
            medianColor: '#185FA5',
            medianWidth: 3,
            whiskerColor: '#888780',
            stemColor: '#888780',
          },
          {
            type: 'scatter',
            name: testedIsin,
            color: TESTED_PRODUCT_COLOR,
            marker: { symbol: 'circle', radius: 7, lineColor: '#FFFFFF', lineWidth: 2 },
            data: rows.map((row, i) => (row.tested != null ? { x: i, y: row.tested } : null)).filter(Boolean),
            dataLabels: {
              enabled: true,
              formatter: function () {
                return formatScorePercent(this.y)
              },
              style: { color: TESTED_PRODUCT_COLOR, textOutline: 'none' },
            },
          },
        ],
      }
    },
  },
  methods: {
    driverLabel(driver) {
      return driver ? this.$t(`drivers.categories.${driver.toUpperCase()}`) : ''
    },
  },
}
</script>
